import { Add, Delete } from '@tunasong/icons'
import type { ProfileGear, StageInput, StageInputStand } from '@tunasong/schemas'
import { stageInputStandLabels, stageInputStands } from '@tunasong/schemas'
import {
  Button,
  Checkbox,
  DebouncedTextField,
  FormControlLabel,
  IconButton,
  Select,
  Typography,
} from '@tunasong/ui-lib'
import type { FC, KeyboardEvent } from 'react'
import { useId, useState } from 'react'
import { ChoiceGroup } from './choice-group.js'
import type { RowRemoval } from './row-types.js'
import { inputRowKey, whoOptions } from './row-types.js'
import { addInputs, removeInput, updateInput } from './setup-edits.js'
import type { NewStageInput } from './setup-edits.js'
import { micDiSuggestions } from './suggestions.js'
import type { FocusRequest } from './use-focus-request.js'
import styles from './rows.module.css'

export interface InputRowsProps {
  inputs: StageInput[]
  sourceSuggestions: string[]
  focus: FocusRequest
  update(edit: (current: ProfileGear | undefined) => ProfileGear): void
  onRemoved(removal: RowRemoval): void
}

const emptyInput: NewStageInput = { source: '', providedByArtist: false }
const addKey = 'add-input'
const rowKey = inputRowKey

const summary = (input: StageInput) =>
  [
    input.micDi?.trim(),
    input.stand && input.stand !== 'none' ? stageInputStandLabels[input.stand] : undefined,
    input.phantomPower ? '48V' : undefined,
    input.providedByArtist ? 'I bring it' : 'Venue provides',
  ]
    .filter(Boolean)
    .join(' · ')

/** Inputs: what needs a channel. One row per channel; a row opens in place for editing. */
export const InputRows: FC<InputRowsProps> = props => {
  const { inputs, sourceSuggestions, update, onRemoved, focus } = props
  const [editingId, setEditingId] = useState<string | null>(null)
  const headingId = useId()
  const sourceListId = useId()
  const micListId = useId()

  const close = (input: StageInput) => {
    setEditingId(null)
    if (!input.source.trim()) {
      // A row that never got a source was never an input: drop it without an Undo
      update(current => removeInput(current, input.id).gear)
      focus.request(addKey)
      return
    }
    focus.request(rowKey(input.id))
  }

  const handleAdd = () => {
    let added: string | undefined
    update(current => {
      const next = addInputs(current, [emptyInput])
      added = next.inputs?.[next.inputs.length - 1]?.id
      return next
    })
    if (added) {
      setEditingId(added)
    }
  }

  const handleDelete = (input: StageInput) => {
    const index = inputs.findIndex(i => i.id === input.id)
    const neighbour = inputs[index + 1] ?? inputs[index - 1]
    let undo: ((current: ProfileGear | undefined) => ProfileGear) | undefined
    update(current => {
      const removal = removeInput(current, input.id)
      undo = removal.undo
      return removal.gear
    })
    setEditingId(null)
    focus.request(neighbour ? rowKey(neighbour.id) : addKey)
    onRemoved({
      message: `Removed ${input.source.trim() || 'input'}.`,
      onUndo: () => {
        if (undo) {
          const restore = undo
          update(current => restore(current))
        }
        focus.request(rowKey(input.id))
      },
    })
  }

  const handleKeyDown = (input: StageInput) => (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      // Handled here: an enclosing dialog must not close as well
      event.stopPropagation()
      close(input)
    }
  }

  const patch = (input: StageInput, change: Partial<NewStageInput>) =>
    update(current => updateInput(current, input.id, change))

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <Typography id={headingId} variant="h6" component="h3" className={styles.title}>
        Inputs
      </Typography>
      <Typography variant="body2" color="textSecondary" className={styles.intro}>
        What needs a channel on the desk.
      </Typography>
      <datalist id={sourceListId}>
        {sourceSuggestions.map(value => (
          <option key={value} value={value} />
        ))}
      </datalist>
      <datalist id={micListId}>
        {micDiSuggestions.map(value => (
          <option key={value} value={value} />
        ))}
      </datalist>
      <ul className={styles.list}>
        {inputs.map(input =>
          input.id === editingId ? (
            <li key={input.id} className={styles.editing}>
              <div
                role="group"
                aria-label={`Input: ${input.source.trim() || 'new'}`}
                className={styles.form}
                onKeyDown={handleKeyDown(input)}
              >
                <DebouncedTextField
                  label="Source"
                  value={input.source}
                  list={sourceListId}
                  autoFocus
                  onDebouncedChange={source => patch(input, { source })}
                />
                <DebouncedTextField
                  label="Mic / DI"
                  value={input.micDi ?? ''}
                  list={micListId}
                  onDebouncedChange={micDi => patch(input, { micDi: micDi.trim() || undefined })}
                />
                <Select
                  label="Stand"
                  value={input.stand ?? ''}
                  onChange={event =>
                    patch(input, { stand: (event.target.value || undefined) as StageInputStand | undefined })
                  }
                >
                  <option value="">Not specified</option>
                  {stageInputStands.map(stand => (
                    <option key={stand} value={stand}>
                      {stageInputStandLabels[stand]}
                    </option>
                  ))}
                </Select>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(input.phantomPower)}
                      onCheckedChange={checked => patch(input, { phantomPower: checked || undefined })}
                    />
                  }
                  label="Needs phantom power (48V)"
                />
                <ChoiceGroup
                  legend="Who brings it"
                  options={whoOptions}
                  value={input.providedByArtist ? 'artist' : 'venue'}
                  onChange={who => patch(input, { providedByArtist: who === 'artist' })}
                />
                <DebouncedTextField
                  label="Notes"
                  value={input.notes ?? ''}
                  onDebouncedChange={notes => patch(input, { notes: notes.trim() || undefined })}
                />
                <div className={styles.formActions}>
                  <Button variant="outlined" color="inherit" onClick={() => handleDelete(input)}>
                    Delete input
                  </Button>
                  <Button variant="contained" onClick={() => close(input)}>
                    Done
                  </Button>
                </div>
              </div>
            </li>
          ) : (
            <li key={input.id} className={styles.row}>
              <button
                type="button"
                className={styles.summary}
                aria-expanded={false}
                ref={focus.register(rowKey(input.id))}
                onClick={() => setEditingId(input.id)}
              >
                <span className={styles.primary}>{input.source.trim() || 'Untitled input'}</span>
                <span className={styles.secondary}>{summary(input)}</span>
              </button>
              <IconButton
                aria-label={`Delete ${input.source.trim() || 'input'}`}
                size="small"
                onClick={() => handleDelete(input)}
              >
                <Delete size={16} />
              </IconButton>
            </li>
          )
        )}
      </ul>
      <Button startIcon={<Add />} ref={focus.register(addKey)} onClick={handleAdd}>
        Add input
      </Button>
    </section>
  )
}

export default InputRows
